import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

// Libs
import {
    hasLowercaseLetter,
    hasNumber,
    hasSpecialCharacter,
    hasUppercaseLetter,
    minLength
} from '@/lib/regex';

// Components
import Icon from '@/components/base/Icon';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';

// Types
import type { InputProps } from '@/components/base/Input';

export type PasswordFieldFormType = { password: string; password_confirmation: string };

const passwordItems = [
    {
        label: 'commons:password_nb_characters',
        regex: minLength(8)
    },
    {
        label: 'commons:password_nb_numbers',
        regex: hasNumber
    },
    {
        label: 'commons:password_nb_uppercase_letter',
        regex: hasUppercaseLetter
    },
    {
        label: 'commons:password_nb_lower_letter',
        regex: hasLowercaseLetter
    },
    {
        label: 'commons:password_nb_special_charaters',
        regex: hasSpecialCharacter
    }
] as const;

const ListChecksPassword = () => {
    const { t } = useTranslation();
    const { watch } = useFormContext<PasswordFieldFormType>();
    const watchPassword = watch('password', '');

    return (
        <ul className="flex flex-wrap font-GSemiBold text-xs text-icoGray-500">
            {passwordItems.map((item, id) => {
                const isComplete = item.regex.test(watchPassword);
                return (
                    <li
                        className={clsx('mb-2 flex w-1/2 items-center', {
                            'line-through': isComplete
                        })}
                        key={id}>
                        <span className="relative -top-px mr-2 inline-block h-1 w-1 rounded-full bg-icoBlue" />
                        {t(item.label)}
                    </li>
                );
            })}
        </ul>
    );
};

type PasswordFieldProps = {
    borderInput?: boolean;
    labels?: boolean;
    confirmPassword?: boolean;
    isPasswordUpdate?: boolean;
    withPlaceholder?: boolean;
} & Pick<InputProps, 'inputStyle'>;

const PasswordField = ({
    borderInput = true,
    labels = true,
    confirmPassword = true,
    isPasswordUpdate = false,
    withPlaceholder = false,
    inputStyle = 'default'
}: PasswordFieldProps) => {
    const { t } = useTranslation();
    const {
        register,
        watch,
        formState: { errors }
    } = useFormContext<PasswordFieldFormType>();
    const [passwordDisplayed, setPasswordDisplayed] = useState(false);
    const [confirmPasswordDisplayed, setConfirmPasswordDisplayed] = useState(false);
    const watchPassword = watch('password', '');
    // Check if password is valid
    const isPasswordValid =
        watchPassword.length > 7 &&
        hasNumber.test(watchPassword) &&
        hasUppercaseLetter.test(watchPassword) &&
        hasLowercaseLetter.test(watchPassword) &&
        hasSpecialCharacter.test(watchPassword);

    function getPasswordPlaceholder() {
        if (withPlaceholder) {
            return t('commons:enter_new_password');
        }
        if (isPasswordUpdate) {
            return t('commons:enter_new_password');
        }
        if (labels) {
            return '';
        }

        return t('commons:your_new_password');
    }

    return (
        <>
            <div className={`${labels ? 'mb-m' : 'my-3'}`}>
                <Label required htmlFor="password" className={clsx({ hidden: !labels })}>
                    {isPasswordUpdate ? t('commons:new_password') : t('commons:password')}
                </Label>
                <Input
                    type={passwordDisplayed ? 'text' : 'password'}
                    id="password"
                    noBorder={!borderInput}
                    inputStyle={inputStyle}
                    placeholder={getPasswordPlaceholder()}
                    valid={isPasswordValid ? t('commons:password_valid') : ''}
                    error={errors?.password?.message}
                    {...register('password', {
                        required: t('errors:error_password_required'),
                        minLength: {
                            value: 8,
                            message: t('errors:error_password_invalid')
                        },
                        validate: {
                            number: (value) =>
                                hasNumber.test(value) || t('errors:error_password_invalid'),
                            upperCaseLetter: (value) =>
                                hasUppercaseLetter.test(value) ||
                                t('errors:error_password_invalid'),
                            lowerCaseLetter: (value) =>
                                hasLowercaseLetter.test(value) ||
                                t('errors:error_password_invalid'),
                            specialCharacter: (value) =>
                                hasSpecialCharacter.test(value) ||
                                t('errors:error_password_invalid')
                        }
                    })}
                    afterInput={
                        <button
                            type="button"
                            id="button-display-password"
                            className="flex items-center transition-colors hover:text-icoBlue"
                            onClick={() => setPasswordDisplayed(!passwordDisplayed)}>
                            <Icon icon={passwordDisplayed ? 'visible' : 'invisible'} />
                        </button>
                    }
                />
            </div>
            <div className="mb-m flex">
                <ListChecksPassword />
            </div>
            {confirmPassword ? (
                <div className="mb-10">
                    <Label
                        required
                        htmlFor="confirmation-password"
                        className={clsx({ hidden: !labels })}>
                        {isPasswordUpdate
                            ? t('commons:confirm_new_password')
                            : t('commons:confirm_password')}
                    </Label>
                    <Input
                        type={confirmPasswordDisplayed ? 'text' : 'password'}
                        id="confirmation-password"
                        noBorder={!borderInput}
                        inputStyle={inputStyle}
                        placeholder={
                            labels && !isPasswordUpdate && !withPlaceholder
                                ? ''
                                : t('commons:confirm_your_password')
                        }
                        error={errors?.password_confirmation?.message}
                        {...register('password_confirmation', {
                            required: t('errors:error_confirm_password_required'),
                            validate: (value) =>
                                value === watchPassword ||
                                t('errors:error_confirm_password_confirmed')
                        })}
                        afterInput={
                            <button
                                type="button"
                                id="button-display-confirm-password"
                                className="flex items-center transition-colors hover:text-icoBlue"
                                onClick={() =>
                                    setConfirmPasswordDisplayed(!confirmPasswordDisplayed)
                                }>
                                <Icon icon={confirmPasswordDisplayed ? 'visible' : 'invisible'} />
                            </button>
                        }
                    />
                </div>
            ) : null}
        </>
    );
};

export default PasswordField;
